/**
 * Geocoding Service
 * Converts zipcodes/postal codes to latitude/longitude coordinates
 * Supports: US ZIP (5-digit), Canadian postal (A1A 1A1), India PIN (6 digits)
 * Uses Nominatim (OpenStreetMap) - FREE, no API key required
 */

import { Platform } from 'react-native';

export interface GeocodingResult {
  lat: number;
  lng: number;
  address?: string;
  country?: string;
  countryCode?: string;
}

class GeocodingService {
  // Primary: Nominatim (OpenStreetMap) - FREE, supports US & Canada
  private nominatimBaseUrl = 'https://nominatim.openstreetmap.org/search';
  
  // Get Nominatim URL (web: direct; mobile: backend proxy base URL for POST)
  private getNominatimUrl(queryParams: string): string {
    if (Platform.OS === 'web') {
      return `https://nominatim.openstreetmap.org/search?${queryParams}`;
    }
    return 'https://bookapt-payment-api.onrender.com/api/geocoding/nominatim';
  }
  
  // Secondary: Google Geocoding API (if you have API key)
  private googleBaseUrl = 'https://maps.googleapis.com/maps/api/geocode/json';
  private googleApiKey: string | null = null; // Set via setGoogleApiKey() if available
  
  // Cache for geocoding results (in-memory cache)
  private geocodingCache: Map<string, GeocodingResult> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Offline fallback: when mobile cannot reach backend or Nominatim (e.g. simulator network),
   * use built-in coords so Explore still works. Add more zips as needed.
   */
  private static readonly OFFLINE_FALLBACK: Record<string, { lat: number; lng: number }> = {
    '48377': { lat: 42.5037582, lng: -83.4347246 }, // Novi, MI (matches Foursquare ll)
    '48390': { lat: 42.53, lng: -83.44 },           // Commerce Township, MI
    '48375': { lat: 42.47, lng: -83.46 },          // Novi (Grand River)
    '48322': { lat: 42.53, lng: -83.44 },          // West Bloomfield, MI
    '48331': { lat: 42.52, lng: -83.44 },          // Farmington Hills, MI
    '48335': { lat: 42.49, lng: -83.43 },          // Farmington Hills, MI
  };

  /**
   * Set Google Maps API key (optional - for better accuracy)
   * Get from: https://console.cloud.google.com/apis/credentials
   */
  setGoogleApiKey(apiKey: string): void {
    this.googleApiKey = apiKey;
  }

  /**
   * Convert zipcode/postal code to lat/lng
   * Supports:
   * - US zipcodes: 12345, 12345-6789
   * - Canadian postal codes: A1A 1A1, A1A1A1
   * - International postal codes (via Nominatim)
   * 
   * Results are cached for 24 hours to avoid redundant API calls
   */
  async zipcodeToLatLng(
    zipcode: string,
    country?: string
  ): Promise<GeocodingResult | null> {
    try {
      // Clean and normalize zipcode
      const effectiveCountry = this.effectiveCountryForPostal(zipcode, country);
      const normalizedZipcode = this.normalizeZipcode(zipcode, effectiveCountry);

      if (!normalizedZipcode) {
        console.warn('Invalid zipcode format:', zipcode);
        return null;
      }

      // Create cache key (include country for accuracy)
      const cacheKey = `${normalizedZipcode}_${effectiveCountry || 'default'}`;
      
      // Check cache first
      const cached = this.geocodingCache.get(cacheKey);
      const expiry = this.cacheExpiry.get(cacheKey);
      if (cached && expiry && Date.now() < expiry) {
        console.log(`✅ Using cached geocoding for ${normalizedZipcode}`);
        return cached;
      }

      // Try Google Geocoding first if API key is available (more accurate)
      let result: GeocodingResult | null = null;
      if (this.googleApiKey) {
        result = await this.geocodeWithGoogle(normalizedZipcode, effectiveCountry);
      }

      // Fallback to Nominatim (free, works for US & Canada)
      if (!result) {
        result = await this.geocodeWithNominatim(normalizedZipcode, effectiveCountry);
      }

      // Mobile-only: when network fails (backend + Nominatim both unreachable), use offline table
      if (!result && Platform.OS !== 'web') {
        const fallback = GeocodingService.OFFLINE_FALLBACK[normalizedZipcode];
        if (fallback) {
          result = {
            lat: fallback.lat,
            lng: fallback.lng,
            countryCode: 'US',
          };
          console.log(`✅ Geocoding (mobile): used offline fallback for ${normalizedZipcode}`);
        }
      }

      if (result) {
        // Cache the result
        this.geocodingCache.set(cacheKey, result);
        this.cacheExpiry.set(cacheKey, Date.now() + this.CACHE_DURATION);
        return result;
      }

      console.warn('Could not geocode zipcode:', zipcode);
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }
  
  /**
   * Batch geocode multiple zipcodes in parallel
   * More efficient than calling zipcodeToLatLng multiple times
   */
  async batchZipcodeToLatLng(
    zipcodes: string[],
    country?: string
  ): Promise<Map<string, GeocodingResult>> {
    const results = new Map<string, GeocodingResult>();
    
    // Remove duplicates
    const uniqueZipcodes = Array.from(new Set(zipcodes));
    
    // Geocode all in parallel
    const promises = uniqueZipcodes.map(async (zipcode) => {
      const result = await this.zipcodeToLatLng(zipcode, country);
      if (result) {
        results.set(zipcode, result);
      }
      return { zipcode, result };
    });
    
    await Promise.all(promises);
    return results;
  }

  /** US: exactly 5 digits (ZIP+4 12345-6789 normalized to 5 digits) */
  private static readonly US_ZIP_REGEX = /^\d{5}$/;
  private static readonly US_ZIP_PLUS4_REGEX = /^\d{5}-\d{4}$/;
  /** Canada: letter-digit-letter space digit-letter-digit (A1A 1A1) */
  private static readonly CA_POSTAL_REGEX = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
  private static readonly CA_POSTAL_NO_SPACE_REGEX = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
  /** India: Postal Index Number (PIN) — 6 digits */
  private static readonly IN_PIN_REGEX = /^\d{6}$/;

  /**
   * Country string used for India (PIN) validation and geocoding.
   */
  private isIndiaCountry(country: string): boolean {
    const n = country.trim().toLowerCase();
    return n === 'india' || n === 'in' || n === 'भारत';
  }

  private isCanadaCountry(country: string): boolean {
    const n = country.trim().toLowerCase();
    return n === 'canada' || n === 'ca';
  }

  /**
   * Pick country for normalization/geocoding when the caller omits it or user left default USA while typing a PIN.
   */
  effectiveCountryForPostal(zipcode: string, country?: string): string {
    const z = (zipcode || '').trim();
    const c = (country || '').trim();

    if (GeocodingService.IN_PIN_REGEX.test(z)) {
      if (this.isCanadaCountry(c)) return c;
      return 'India';
    }

    if (c) return c;

    const cleaned = z.toUpperCase();
    const noSpaces = cleaned.replace(/\s/g, '');
    if (
      GeocodingService.CA_POSTAL_NO_SPACE_REGEX.test(noSpaces) ||
      GeocodingService.CA_POSTAL_REGEX.test(cleaned)
    ) {
      return 'Canada';
    }
    return 'USA';
  }

  /**
   * Normalize zipcode format
   * - India (when country is India): 6 digits only
   * - US: 12345 or 12345-6789 → 12345 (5 digits)
   * - Canada: A1A 1A1 or A1A1A1 → A1A 1A1 (space in middle)
   */
  private normalizeZipcode(zipcode: string, country?: string): string {
    if (!zipcode || typeof zipcode !== 'string') return '';
    const cleaned = zipcode.trim().toUpperCase();
    if (!cleaned) return '';

    if (country && this.isIndiaCountry(country)) {
      const digits = zipcode.trim().replace(/\D/g, '');
      return GeocodingService.IN_PIN_REGEX.test(digits) ? digits : '';
    }

    // US zipcode: Remove extended format (12345-6789 → 12345)
    if (GeocodingService.US_ZIP_PLUS4_REGEX.test(cleaned)) {
      return cleaned.substring(0, 5);
    }
    // US zipcode: 5 digits only
    if (GeocodingService.US_ZIP_REGEX.test(cleaned)) {
      return cleaned;
    }
    // Canadian postal code: 6 chars letter-digit-letter digit-letter-digit (with or without space)
    const noSpaces = cleaned.replace(/\s/g, '');
    if (GeocodingService.CA_POSTAL_NO_SPACE_REGEX.test(noSpaces)) {
      return `${noSpaces.substring(0, 3)} ${noSpaces.substring(3)}`;
    }
    if (GeocodingService.CA_POSTAL_REGEX.test(cleaned)) {
      return cleaned;
    }
    return '';
  }

  /**
   * Geocode using Nominatim (OpenStreetMap) - FREE
   * Supports: US, Canada, and most international postal codes
   * Mobile: tries backend proxy first; on network failure falls back to direct Nominatim (per usage policy).
   */
  private async geocodeWithNominatim(
    zipcode: string,
    country?: string
  ): Promise<GeocodingResult | null> {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        postalcode: zipcode,
        format: 'json',
        limit: '1',
        addressdetails: '1',
      });
      
      // Add country filter if provided
      if (country) {
        params.append('countrycodes', this.getCountryCode(country));
      } else {
        params.append('countrycodes', 'us,ca,in');
      }

      const isWeb = Platform.OS === 'web';
      const timeoutMs = isWeb ? 10000 : 65000;
      const maxAttempts = isWeb ? 1 : 2;
      const retryDelayMs = 2500; // Give Render time to wake on cold start

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          let response: any;
          const url = this.getNominatimUrl(params.toString());

          if (isWeb) {
            const headers: Record<string, string> = { 'User-Agent': 'BookAPT/1.0' };
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
            try {
              response = await fetch(url, { headers, signal: controller.signal });
            } finally {
              clearTimeout(timeoutId);
            }
          } else {
            // Mobile: POST with JSON body so backend always receives postalcode
            const backendUrl = 'https://bookapt-payment-api.onrender.com/api/geocoding/nominatim';
            const body = {
              postalcode: zipcode,
              countrycodes: country ? this.getCountryCode(country) : 'us,ca',
              format: 'json',
              limit: '1',
              addressdetails: '1',
            };
            const timeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => {
                const err: any = new Error(`Timeout after ${timeoutMs}ms`);
                err.name = 'TimeoutError';
                reject(err);
              }, timeoutMs)
            );
            response = await Promise.race([
              fetch(backendUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(body),
              }),
              timeoutPromise,
            ]);
          }

          if (!response.ok) {
            let backendError = '';
            try {
              const errBody = await response.json();
              if (errBody && typeof (errBody as any).error === 'string') backendError = (errBody as any).error;
            } catch (_) {}
            if (response.status === 400 && backendError) {
              console.warn(`⚠️ Geocoding (mobile): backend 400 — ${backendError}`);
              return null;
            }
            if (!isWeb && response.status === 404 && (url || '').includes('/api/geocoding/nominatim')) {
              console.warn('⚠️ Geocoding (mobile): backend proxy not found (404). Redeploy backend with POST /api/geocoding/nominatim.');
            }
            const retryableStatus = [502, 503, 504].includes(response.status);
            if (!isWeb && retryableStatus && attempt < maxAttempts) {
              console.warn(`⚠️ Geocoding (mobile): backend ${response.status}. Retrying... (${attempt}/${maxAttempts})`);
              await new Promise<void>(r => setTimeout(() => r(), retryDelayMs));
              continue;
            }
            console.warn(`⚠️ Nominatim API returned status ${response.status} for zipcode ${zipcode}`);
            return null;
          }

          const data = await response.json();

          if (!data) {
            console.warn(`⚠️ Geocoding: empty response for zipcode ${zipcode}`);
            return null;
          }
          if (!Array.isArray(data)) {
            const err = (data as any)?.error ?? 'Not an array';
            console.warn(`⚠️ Geocoding: backend returned non-array for zipcode ${zipcode}:`, err);
            return null;
          }
          if (data.length === 0) {
            console.warn(`⚠️ No geocoding results found for zipcode ${zipcode}`);
            return null;
          }

          const result = data[0];
          const lat = result?.lat != null ? parseFloat(String(result.lat)) : NaN;
          const lng = result?.lon != null ? parseFloat(String(result.lon)) : NaN;
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            console.warn(`⚠️ Geocoding: invalid lat/lon in response for zipcode ${zipcode}`);
            return null;
          }

          return {
            lat,
            lng,
            address: result?.display_name,
            country: result?.address?.country,
            countryCode: result?.address?.country_code?.toUpperCase(),
          };
        } catch (fetchError: any) {
          const isTimeout =
            fetchError?.name === 'AbortError' || fetchError?.name === 'TimeoutError';
          const isNetwork =
            fetchError?.message?.includes('Network request failed') ||
            fetchError?.message?.includes('Failed to fetch') ||
            fetchError?.code === 'ENOTFOUND' ||
            fetchError?.code === 'ECONNREFUSED';

          if (!isWeb && (isTimeout || isNetwork) && attempt < maxAttempts) {
            console.warn(
              `⚠️ Geocoding (mobile): ${isTimeout ? 'timeout' : 'network error'} for zipcode ${zipcode}. Retrying... (${attempt}/${maxAttempts})`
            );
            await new Promise<void>(r => setTimeout(() => r(), retryDelayMs));
            continue;
          }

          // Mobile: if backend unreachable after retries, try direct Nominatim (per usage policy)
          if (!isWeb && (isTimeout || isNetwork)) {
            const direct = await this.fetchNominatimDirect(zipcode, country, params.toString(), timeoutMs);
            if (direct) return direct;
          }

          if (isTimeout) {
            console.warn(`⚠️ Nominatim geocoding timeout for zipcode ${zipcode} (${timeoutMs / 1000}s limit)`);
            return null;
          }

          if (isNetwork) {
            console.warn(`⚠️ Network error geocoding zipcode ${zipcode}:`, fetchError.message);
            console.warn('💡 Backend was unreachable; direct Nominatim fallback also failed or was skipped.');
            return null;
          }

          throw fetchError;
        }
      }
    } catch (error: any) {
      console.error('❌ Nominatim geocoding error:', error?.message || error);
      return null;
    }
    return null;
  }

  /**
   * Mobile-only fallback: call Nominatim directly when backend proxy is unreachable.
   * Uses a unique User-Agent per Nominatim usage policy.
   */
  private async fetchNominatimDirect(
    zipcode: string,
    country: string | undefined,
    queryString: string,
    timeoutMs: number
  ): Promise<GeocodingResult | null> {
    try {
      const directUrl = `https://nominatim.openstreetmap.org/search?${queryString}`;
      const headers: Record<string, string> = {
        'User-Agent': 'BookAPT/1.0 (mobile; https://bookapt.io)',
        'Accept': 'application/json',
      };
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => {
          const err: any = new Error('Timeout');
          err.name = 'TimeoutError';
          reject(err);
        }, Math.min(timeoutMs, 15000))
      );
      const response = await Promise.race([
        fetch(directUrl, { headers }),
        timeoutPromise,
      ]);
      if (!response.ok) return null;
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) return null;
      const result = data[0];
      const lat = result?.lat != null ? parseFloat(String(result.lat)) : NaN;
      const lng = result?.lon != null ? parseFloat(String(result.lon)) : NaN;
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      console.log(`✅ Geocoding (mobile): used direct Nominatim fallback for ${zipcode}`);
      return {
        lat,
        lng,
        address: result?.display_name,
        country: result?.address?.country,
        countryCode: result?.address?.country_code?.toUpperCase(),
      };
    } catch (e) {
      console.warn('⚠️ Geocoding (mobile): direct Nominatim fallback failed:', (e as Error)?.message);
      return null;
    }
  }

  /**
   * Geocode using Google Geocoding API (optional - requires API key)
   * More accurate but requires billing setup
   */
  private async geocodeWithGoogle(
    zipcode: string,
    country?: string
  ): Promise<GeocodingResult | null> {
    if (!this.googleApiKey) {
      return null;
    }

    try {
      let query = `address=${encodeURIComponent(zipcode)}`;
      
      // Add country filter if provided
      if (country) {
        query += `&region=${this.getCountryCode(country)}`;
      }

      const url = `${this.googleBaseUrl}?${query}&key=${this.googleApiKey}`;
      
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Google Geocoding API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        return null;
      }

      const result = data.results[0];
      const location = result.geometry.location;
      
      // Extract country from address components
      const countryComponent = result.address_components?.find(
        (component: any) => component.types.includes('country')
      );

      return {
        lat: location.lat,
        lng: location.lng,
        address: result.formatted_address,
        country: countryComponent?.long_name,
        countryCode: countryComponent?.short_name?.toUpperCase(),
      };
    } catch (error) {
      console.error('Google geocoding error:', error);
      return null;
    }
  }

  /**
   * Convert country name to ISO country code
   */
  private getCountryCode(country: string): string {
    const countryMap: { [key: string]: string } = {
      'usa': 'us',
      'united states': 'us',
      'us': 'us',
      'canada': 'ca',
      'ca': 'ca',
      india: 'in',
      in: 'in',
      'united kingdom': 'gb',
      'uk': 'gb',
      'australia': 'au',
      'au': 'au',
    };

    const normalized = country.toLowerCase().trim();
    return countryMap[normalized] || normalized.substring(0, 2).toLowerCase();
  }

  /**
   * True if zipcode is valid US format: exactly 5 digits (or 5+4 normalized to 5).
   */
  isUSZipcode(zipcode: string): boolean {
    const normalized = this.normalizeZipcode(zipcode);
    return GeocodingService.US_ZIP_REGEX.test(normalized);
  }

  /**
   * True if zipcode is valid Canadian format: A1A 1A1 (letter-digit-letter space digit-letter-digit).
   */
  isCanadianPostalCode(zipcode: string): boolean {
    const normalized = this.normalizeZipcode(zipcode);
    return GeocodingService.CA_POSTAL_REGEX.test(normalized);
  }

  /**
   * True if value is a valid India PIN (6 digits), when country context is India.
   */
  isIndiaPinCode(zipcode: string, country?: string): boolean {
    if (!zipcode || typeof zipcode !== 'string') return false;
    const effective = this.effectiveCountryForPostal(zipcode, country);
    if (!this.isIndiaCountry(effective)) return false;
    const normalized = this.normalizeZipcode(zipcode, 'India');
    return GeocodingService.IN_PIN_REGEX.test(normalized);
  }

  /**
   * Validate postal/zip: US (5 digits), Canada (A1A 1A1), or India PIN (6 digits).
   * Pass `country` from the form when possible; six-digit values are treated as India unless country is Canada.
   */
  isValidZipcode(zipcode: string, country?: string): boolean {
    if (!zipcode || typeof zipcode !== 'string') return false;
    const effective = this.effectiveCountryForPostal(zipcode, country);
    if (this.isIndiaCountry(effective)) {
      const normalized = this.normalizeZipcode(zipcode, 'India');
      return GeocodingService.IN_PIN_REGEX.test(normalized);
    }
    const normalized = this.normalizeZipcode(zipcode);
    if (!normalized) return false;
    return this.isUSZipcode(normalized) || this.isCanadianPostalCode(normalized);
  }

  /**
   * Return normalized postal for storage: US 5 digits, Canada A1A 1A1, India 6-digit PIN. Empty if invalid.
   */
  normalizeZipcodeForStorage(zipcode: string, country?: string): string {
    if (!zipcode || typeof zipcode !== 'string') return '';
    const effective = this.effectiveCountryForPostal(zipcode, country);
    if (this.isIndiaCountry(effective)) {
      const normalized = this.normalizeZipcode(zipcode, 'India');
      return this.isValidZipcode(zipcode, country) ? normalized : '';
    }
    const normalized = this.normalizeZipcode(zipcode);
    return this.isValidZipcode(zipcode, country) ? normalized : '';
  }
}

// Export singleton instance
export const geocodingService = new GeocodingService();

// Export class for testing
export default GeocodingService;


